import { createContext, useContext, useCallback, useState, type ReactNode } from 'react';

type DecisionsStore = {
  scenario_run_id?: string;
  run_id?: string;
  period_from?: string;
  period_to?: string;
  ref_experiment_id?: string;
};

type DecisionsContextValue = {
  store: DecisionsStore;
  get: (key: keyof DecisionsStore) => string | null;
  set: (key: keyof DecisionsStore, value: string | undefined) => void;
};

const DecisionsContext = createContext<DecisionsContextValue | null>(null);

export function DecisionsProvider({ children }: { children: ReactNode }) {
  const [store, setStore] = useState<DecisionsStore>({});

  const get = useCallback((key: keyof DecisionsStore): string | null => store[key] ?? null, [store]);

  const set = useCallback((key: keyof DecisionsStore, value: string | undefined) => {
    setStore((prev) => {
      if (!value) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: value };
    });
  }, []);

  return <DecisionsContext.Provider value={{ store, get, set }}>{children}</DecisionsContext.Provider>;
}

export function useDecisionsContext(): DecisionsContextValue {
  const ctx = useContext(DecisionsContext);
  if (!ctx) throw new Error('useDecisionsContext must be used within a DecisionsProvider');
  return ctx;
}
